import axiosV2Instance from './axiosV2Instance';
import { Media } from './usersApi';

export interface WallPost {
  id: number;
  userId: number;
  userName: string;
  userAvatar?: Media | null | undefined;
  siteId?: number | null; // Привязка к объекту
  siteName?: string | null; // Название объекта
  siteAvatar?: Media | null | undefined; // Аватарка объекта
  content: string;
  images?: Media[];
  hashtags?: string[]; // Список хештегов
  likes: number;
  comments: number;
  views: number; // Количество уникальных просмотров
  isLiked: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateWallPostRequest {
  content: string;
  siteId?: number | null;
  images?: File[];
}

export interface WallPostComment {
  id: number;
  postId: number;
  userId: number;
  userName: string;
  userAvatar?: Media | null | undefined;
  content: string;
  createdAt: string;
}

class WallPostsApi {
  private baseUrl = '/wall-posts';

  // Получить все посты (лента всех постов)
  async getAllPosts(limit: number = 50, offset: number = 0): Promise<WallPost[]> {
    try {
      const response = await axiosV2Instance.get<{ posts: WallPost[], total: number, hasMore: boolean }>(
        `${this.baseUrl}/feed`,
        { params: { limit, offset } }
      );
      return response.data.posts || [];
    } catch (error) {
      console.error('Error fetching all posts:', error);
      // В случае ошибки возвращаем пустой массив
      return [];
    }
  }

    // Получить посты объекта (стена объекта — публикации всех участников)
    async getSitePosts(siteId: number, limit: number = 20, offset: number = 0): Promise<WallPost[]> {
        try {
            const response = await axiosV2Instance.get<{ posts: WallPost[], total: number, hasMore: boolean }>(
                `${this.baseUrl}/site/${siteId}`,
                { params: { limit, offset } }
            );
            return response.data.posts || [];
        } catch (error) {
            console.error('Error fetching site posts:', error);
            return [];
        }
    }

    // Получить посты пользователя
    async getUserPosts(userId: number, limit: number = 20, offset: number = 0, siteId?: number | null): Promise<WallPost[]> {
        try {
            const params: { limit: number; offset: number; siteId?: number } = { limit, offset };
            if (siteId) {
                params.siteId = siteId;
            }
            const response = await axiosV2Instance.get<{ posts: WallPost[], total: number, hasMore: boolean }>(
                `${this.baseUrl}/user/${userId}`,
                { params }
            );
            return response.data.posts;
        } catch (error) {
            console.error('Error fetching user posts:', error);
            // В случае ошибки возвращаем моковые данные для демонстрации
            return this.getMockPosts(userId);
        }
    }

    // Создать новый пост
    async createPost(request: CreateWallPostRequest): Promise<WallPost> {
        try {
            const formData = new FormData();
            formData.append('content', request.content || '');
            
            // Добавляем siteId, если указан
            if (request.siteId) {
                formData.append('siteId', request.siteId.toString());
            }
            
            // Добавляем изображения, если они есть
            if (request.images && request.images.length > 0) {
                console.log(`Добавляем ${request.images.length} изображений в FormData`);
                
                request.images.forEach((image, index) => {
                    // Проверяем, что файл валиден
                    if (!image || !(image instanceof File)) {
                        console.warn(`Файл ${index} не является валидным File объектом`);
                        return;
                    }
                    
                    console.log(`Добавляем изображение ${index + 1}:`, {
                        name: image.name,
                        size: image.size,
                        type: image.type
                    });
                    
                    // Используем одно и то же имя поля 'images' для всех файлов
                    // Сервер должен обрабатывать это как массив
                    formData.append('images', image, image.name);
                });
            }

            console.log('Отправляем FormData:', {
                hasContent: !!request.content,
                hasSiteId: !!request.siteId,
                imagesCount: request.images?.length || 0
            });

            const response = await axiosV2Instance.post<WallPost>(this.baseUrl, formData, {
                // Content-Type будет установлен автоматически интерцептором axios для FormData
                // Увеличиваем таймаут для больших файлов (особенно важно для мобильных)
                timeout: 120000, // 120 секунд для множественной загрузки
                // Отслеживаем прогресс загрузки
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        console.log(`Upload progress: ${pct}%`);
                    }
                }
            });
            
            console.log('Пост успешно создан:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating post:', error);
            console.error('Error details:', {
                message: error?.message,
                response: error?.response?.data,
                status: error?.response?.status,
                statusText: error?.response?.statusText
            });
            throw error;
        }
    }

  async updatePost(postId: number, params: {
    content: string;
    newImages?: File[];
    removeMediaIds?: number[];
    coverMediaId?: number | null;
  }): Promise<WallPost> {
    const formData = new FormData();
    formData.append('content', params.content);
    if (params.coverMediaId != null) formData.append('coverMediaId', String(params.coverMediaId));
    params.removeMediaIds?.forEach(id => formData.append('removeMediaIds', String(id)));
    params.newImages?.forEach(f => formData.append('images', f, f.name));
    const response = await axiosV2Instance.put<WallPost>(`${this.baseUrl}/${postId}`, formData, { timeout: 120000 });
    return response.data;
  }

  // Удалить пост
  async deletePost(postId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`${this.baseUrl}/${postId}`);
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }

    // Лайкнуть/убрать лайк с поста
    async toggleLike(postId: number): Promise<{ isLiked: boolean }> {
        try {
            const response = await axiosV2Instance.post<{ isLiked: boolean }>(`${this.baseUrl}/${postId}/like`);
            return response.data;
        } catch (error) {
            console.error('Error toggling like:', error);
            throw error;
        }
    }

  // Получить комментарии к посту
  async getPostComments(postId: number): Promise<WallPostComment[]> {
    try {
      const response = await axiosV2Instance.get<WallPostComment[]>(
        `${this.baseUrl}/${postId}/comments`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching post comments:', error);
      return [];
    }
  }

  // Создать комментарий к посту
  async createComment(postId: number, content: string): Promise<WallPostComment> {
    try {
      const response = await axiosV2Instance.post<WallPostComment>(
        `${this.baseUrl}/${postId}/comments`,
        { content }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  }

  // Зарегистрировать просмотр поста
  async registerView(postId: number): Promise<{ isNewView: boolean; message: string }> {
    try {
      const response = await axiosV2Instance.post<{ isNewView: boolean; message: string }>(
        `${this.baseUrl}/${postId}/view`
      );
      return response.data;
    } catch (error) {
      // Игнорируем ошибки регистрации просмотра - это не критично
      console.debug('Error registering view for post:', postId, error);
      return { isNewView: false, message: 'Не удалось зарегистрировать просмотр' };
    }
  }

  // Моковые данные для демонстрации (пока бэкенд не готов)
  private getMockPosts(userId: number): WallPost[] {
    return [
      {
        id: 1,
        userId: userId,
        userName: 'Current User',
        content: 'Отличный день для работы! Завершил важный проект 🎉',
        images: [],
        likes: 12,
        comments: 3,
        views: 45,
        isLiked: false,
        createdAt: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 2,
        userId: userId,
        userName: 'Current User',
        content: 'Изучаю новые технологии. React + TypeScript = ❤️',
        images: [],
        likes: 25,
        comments: 7,
        views: 89,
        isLiked: true,
        createdAt: new Date(Date.now() - 86400000).toISOString()
      },
      {
        id: 3,
        userId: userId,
        userName: 'Current User',
        content: 'Только что завершил новую фичу для проекта! Добавил личную страницу профиля с постами. Теперь можно делиться своими мыслями и достижениями прямо в системе.',
        images: [],
        likes: 8,
        comments: 2,
        views: 34,
        isLiked: false,
        createdAt: new Date(Date.now() - 172800000).toISOString()
      }
    ];
  }
}

export const wallPostsApi = new WallPostsApi();

